import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL } from "@/mocks/categories";

export default function ContentSections() {
  const { t } = useTranslation();

  const topics = [
    { icon: "ri-grid-line", title: t("topic_1_title"), desc: t("topic_1_desc"), image: "https://readdy.ai/api/search-image?query=Clean%20spreadsheet%20with%20product%20category%20columns%20on%20a%20soft%20sage%20green%20desk%2C%20minimal%20editorial%20style%2C%20soft%20natural%20light%2C%20high%20detail&width=600&height=400&seq=topic-01&orientation=landscape" },
    { icon: "ri-star-line", title: t("topic_2_title"), desc: t("topic_2_desc"), image: "https://readdy.ai/api/search-image?query=Review%20rating%20stars%20and%20notes%20on%20a%20soft%20coral%20pastel%20surface%2C%20minimal%20editorial%20style%2C%20soft%20natural%20light%2C%20high%20detail&width=600&height=400&seq=topic-02&orientation=landscape" },
    { icon: "ri-truck-line", title: t("topic_3_title"), desc: t("topic_3_desc"), image: "https://readdy.ai/api/search-image?query=Neat%20shipping%20boxes%20with%20tape%20measure%20on%20a%20soft%20sand%20beige%20background%2C%20minimal%20editorial%20style%2C%20soft%20natural%20light%2C%20high%20detail&width=600&height=400&seq=topic-03&orientation=landscape" },
    { icon: "ri-coupon-3-line", title: t("topic_4_title"), desc: t("topic_4_desc"), image: "https://readdy.ai/api/search-image?query=Discount%20coupon%20tickets%20on%20a%20soft%20lemon%20yellow%20background%2C%20minimal%20editorial%20style%2C%20soft%20natural%20light%2C%20high%20detail&width=600&height=400&seq=topic-04&orientation=landscape" },
    { icon: "ri-store-3-line", title: t("topic_5_title"), desc: t("topic_5_desc"), image: "https://readdy.ai/api/search-image?query=Tidy%20warehouse%20shelves%20with%20labeled%20boxes%20on%20a%20soft%20teal%20tinted%20scene%2C%20minimal%20editorial%20style%2C%20soft%20natural%20light%2C%20high%20detail&width=600&height=400&seq=topic-05&orientation=landscape" },
    { icon: "ri-shield-check-line", title: t("topic_6_title"), desc: t("topic_6_desc"), image: "https://readdy.ai/api/search-image?query=Trust%20shield%20symbol%20with%20checkmark%20on%20a%20soft%20sage%20green%20background%2C%20minimal%20editorial%20style%2C%20soft%20natural%20light%2C%20high%20detail&width=600&height=400&seq=topic-06&orientation=landscape" },
  ];

  const values = [
    { icon: "ri-search-eye-line", title: t("value_1_title"), desc: t("value_1_desc") },
    { icon: "ri-scales-3-line", title: t("value_2_title"), desc: t("value_2_desc") },
    { icon: "ri-verified-badge-line", title: t("value_3_title"), desc: t("value_3_desc") },
  ];

  return (
    <>
      {/* Search / popular topics */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">{t("search_title")}</h2>
          </div>

          <div className="mt-8 max-w-xl mx-auto">
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-background-300 bg-background-50">
              <i className="ri-search-line text-foreground-400 text-lg" />
              <input
                type="text"
                placeholder={t("search_placeholder")}
                className="w-full bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
              />
            </div>
            <p className="mt-4 text-xs text-foreground-500">{t("search_popular")}:</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(t("search_tags", { returnObjects: true }) as string[]).map((tag, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium cursor-pointer hover:bg-secondary-200 transition-colors whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Topics grid */}
          <div className="mt-12">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center">
              {t("topics_title")}
            </h3>
            <p className="mt-2 text-sm text-foreground-600 text-center">{t("topics_subtitle")}</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {topics.map((topic, i) => (
                <div key={i} className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                  <div className="w-full h-36 md:h-40 overflow-hidden">
                    <img
                      src={topic.image}
                      alt={topic.title}
                      title={`${topic.title} - USFans Spreadsheet`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-5 md:p-6">
                    <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className={`${topic.icon} text-xl`} />
                    </span>
                    <h4 className="mt-4 font-heading font-semibold text-foreground-950 text-base">{topic.title}</h4>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{topic.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core value */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">{t("value_title")}</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{t("value_subtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {values.map((v, i) => (
              <div key={i} className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200 text-center">
                <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={`${v.icon} text-2xl`} />
                </span>
                <h3 className="mt-5 font-heading font-bold text-foreground-950 text-lg tracking-wide">{v.title}</h3>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured spreadsheet */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-secondary-950">
        <div className="max-w-[1280px] mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50">{t("featured_title")}</h2>
          <p className="mt-4 text-sm md:text-base text-background-300 max-w-2xl mx-auto leading-relaxed">
            {t("featured_subtitle")}
          </p>
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-8 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-primary-500 text-background-50 font-semibold text-sm cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            {t("featured_cta")}
            <i className="ri-arrow-right-up-line" />
          </a>
        </div>
      </section>

      {/* Comparison table */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 text-center">
            {t("compare_title")}
          </h2>
          <div className="mt-8 overflow-x-auto rounded-lg border border-background-200">
            <table className="w-full min-w-[600px] bg-background-50">
              <thead>
                <tr className="bg-background-100">
                  <th className="px-5 py-4 text-left text-sm font-heading font-semibold text-foreground-950 whitespace-nowrap">
                    {t("compare_col_topic")}
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-heading font-semibold text-foreground-950 whitespace-nowrap">
                    {t("compare_col_best")}
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-heading font-semibold text-foreground-950 whitespace-nowrap">
                    {t("compare_col_info")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(t("compare_rows", { returnObjects: true }) as string[][]).map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-background-50" : "bg-background-100/60"}>
                    <td className="px-5 py-4 text-sm font-medium text-primary-700 whitespace-nowrap">{row[0]}</td>
                    <td className="px-5 py-4 text-sm text-foreground-700">{row[1]}</td>
                    <td className="px-5 py-4 text-sm text-foreground-700">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Trust section */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">{t("trust_title")}</h2>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{t("trust_subtitle")}</p>
              <Link
                to="/articles/what-is-usfans-spreadsheet"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md border border-foreground-300 text-foreground-900 font-semibold text-sm cursor-pointer hover:bg-background-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-shield-check-line" />
                {t("trust_cta")}
              </Link>
            </div>
            <div className="rounded-lg overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20flat%20lay%20of%20organized%20fashion%20items%20with%20a%20notebook%20and%20checklist%20on%20a%20soft%20cream%20background%2C%20fresh%20airy%20editorial%20style%2C%20soft%20natural%20light%2C%20high%20detail&width=800&height=600&seq=trust-01&orientation=landscape"
                alt={t("trust_title")}
                title="USFans Spreadsheet safety"
                className="w-full h-56 md:h-72 object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}